<template>
  <header ref="headerRoot" class="dashboard-header" role="banner">
    <div class="header-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <router-link to="/" class="logo-link" aria-label="Navigate to home">
          <img 
            src="@/assets/logo.png" 
            alt="Nexthome4U Logo" 
            class="logo-image"
            loading="lazy"
          />
        </router-link>
      </div>

      <!-- Object Name Display -->
      <div class="object-name" v-if="objectName" :title="objectName">
        <h1 class="object-title">{{ objectName }}</h1>
      </div>

      <!-- Profile Section -->
      <div class="profile-section">
        <!-- Notification Bell -->
        <div v-if="objectName" class="notification-bell-container" ref="notificationDropdown">
          <button 
            class="notification-bell-btn" 
            @click="toggleNotificationDropdown" 
            aria-label="View new bookings"
            :aria-expanded="showNotificationDropdown"
          >
            <font-awesome-icon :icon="['fas', 'bell']" class="bell-icon" />
            <span v-if="newBookingsCount > 0" class="notification-count-badge">{{ newBookingsCount }}</span>
          </button>
          
          <!-- Notification Dropdown -->
          <transition name="notification-dropdown">
            <div v-if="showNotificationDropdown && newBookingsCount > 0" class="notification-dropdown-menu" role="menu">
              <div class="notification-dropdown-header">
                <div class="notification-dropdown-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </div>
                <div class="notification-dropdown-content">
                  <div class="notification-dropdown-title">Notifications</div>
                  <div class="notification-dropdown-message">
                    You have {{ newBookingsCount }} {{ newBookingsCount === 1 ? 'New Booking' : 'New Bookings' }}
                  </div>
                </div>
              </div>
              <div class="notification-dropdown-footer">
                <button 
                  class="notification-view-btn" 
                  @click="handleViewNewBookings"
                  role="menuitem"
                >
                  View All Bookings
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </transition>
        </div>
        
        <div class="admin-info">
          <span class="admin-name">
            {{ adminProfileData.providerAdminName || 'Admin' }}
          </span>
          <span class="admin-role">{{ userRoleDisplay }}</span>
        </div>
        
        <div class="profile-dropdown" ref="profileDropdown">
          <button 
            class="profile-button"
            @click="toggleDropdown"
            :aria-expanded="showDropdown"
            aria-haspopup="true"
            aria-label="Open user menu"
          >
            <img 
              :src="adminProfileData.profilepic || require('@/assets/profile.png')" 
              alt="User profile" 
              class="profile-image"
              loading="lazy"
            />
            <svg class="dropdown-icon" :class="{ 'rotated': showDropdown }" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>

          <transition name="dropdown-fade">
            <div v-if="showDropdown" class="dropdown-menu" role="menu">
              <button 
                @click="openEditProfile" 
                class="dropdown-item"
                role="menuitem"
              >
                <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                </svg>
                Edit Profile
              </button>
              <button 
                @click="openManageSubscription" 
                class="dropdown-item"
                role="menuitem"
              >
                <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v2H6V6zm0 4h5v2H6v-2z" clip-rule="evenodd"/>
                </svg>
                Manage subscription
              </button>
              <button 
                @click="logout" 
                class="dropdown-item logout-item"
                role="menuitem"
              >
                <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd"/>
                </svg>
                Logout
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>

  </header>

  <teleport to="body">
    <transition name="logout-overlay-fade">
      <div
        v-if="isLoggingOut"
        class="logout-processing-overlay"
        role="presentation"
      >
        <div
          class="logout-processing-card"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="logout-processing-spinner-wrap" aria-hidden="true">
            <div class="logout-processing-spinner" />
          </div>
          <h2 class="logout-processing-title">Signing out</h2>
          <p class="logout-processing-subtitle">
            Please wait while we securely end your session.
          </p>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import notificationService from '@/services/notificationService';
import { clearRefreshToken } from '@/utilities/refreshTokenStorage';

library.add(faBell);

export default {
  name: 'DashboardHeader',
  components: {
    FontAwesomeIcon
  },
  props: {
    objectName: {
      type: String,
      default: '',
      validator: (value) => typeof value === 'string'
    }
  },
  data() {
    return {
      showDropdown: false,
      adminProfileData: {},
      clickOutsideHandler: null,
      newBookingsCount: 0,
      showNotificationDropdown: false,
      notificationClickOutsideHandler: null,
      notificationUpdateKey: 0,
      isLoggingOut: false,
    }
  },
  computed: {
    displayName() {
      return this.adminProfileData.providerAdminName || 'Admin';
    },
    hasObjectName() {
      return this.objectName && this.objectName.trim().length > 0;
    },
    userRoleDisplay() {
      const userType = this.adminProfileData?.userType;
      if (userType === 'MASTER') {
        return 'MASTER';
      } else if (userType === 'SUBUSER') {
        return 'SUBUSER';
      } else if (userType === 'OPERATOR') {
        return 'OPERATOR';
      }
      return 'Administrator'; // Fallback for unknown types
    }
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      
      if (this.showDropdown) {
        this.$nextTick(() => {
          // Focus management for accessibility
          const firstMenuItem = this.$refs.headerRoot?.querySelector('.dropdown-item');
          if (firstMenuItem) {
            firstMenuItem.focus();
          }
        });
      }
    },
    
    closeDropdown() {
      this.showDropdown = false;
    },
    
    handleClickOutside(event) {
      if (this.$refs.profileDropdown && !this.$refs.profileDropdown.contains(event.target)) {
        this.closeDropdown();
      }
    },
    
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.showDropdown) {
        this.closeDropdown();
        // Return focus to the profile button
        this.$refs.headerRoot?.querySelector('.profile-button')?.focus();
      }
    },
    
    async logout() {
      if (this.isLoggingOut) {
        return;
      }
      this.isLoggingOut = true;
      await this.$nextTick();

      try {
        this.closeDropdown();
        try {
          await apiService.logout();
        } catch (e) {
          console.warn('Server logout failed:', e);
        }
        clearRefreshToken();

        const deviceToken = localStorage.getItem('DEVICE_REG_TOKEN');

        localStorage.clear();

        if (deviceToken) {
          localStorage.setItem('DEVICE_REG_TOKEN', deviceToken);
        }

        await this.$router.push('/login');
      } catch (error) {
        console.error('Logout failed:', error);
        try {
          await this.$router.push('/login');
        } catch (navError) {
          console.error('Navigation to login failed:', navError);
        } finally {
          this.isLoggingOut = false;
        }
      }
    },
    
    loadAdminProfileData() {
      try {
        const storedData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = storedData ? JSON.parse(storedData) : {};
      } catch (error) {
        console.error('Failed to parse admin profile data:', error);
        this.adminProfileData = {};
      }
    },
    async fetchBookingNotifications(forceRefresh = false) {
      if (!this.objectName) return; // Only fetch if we're in an object dashboard
      const objectId = this.$route.params.id;
      if (objectId) {
        // Get from centralized notification service
        await notificationService.getNotifications(forceRefresh);
        this.updateNotificationCount();
      } else {
        this.newBookingsCount = 0;
      }
    },
    toggleNotificationDropdown() {
      // Only show dropdown if there are notifications
      if (this.newBookingsCount > 0) {
        this.showNotificationDropdown = !this.showNotificationDropdown;
        
        if (this.showNotificationDropdown) {
          this.$nextTick(() => {
            this.setupNotificationClickOutside();
          });
        } else {
          this.removeNotificationClickOutside();
        }
      }
    },
    
    closeNotificationDropdown() {
      this.showNotificationDropdown = false;
      this.removeNotificationClickOutside();
    },
    
    setupNotificationClickOutside() {
      this.notificationClickOutsideHandler = (event) => {
        if (this.$refs.notificationDropdown && !this.$refs.notificationDropdown.contains(event.target)) {
          this.closeNotificationDropdown();
        }
      };
      document.addEventListener('click', this.notificationClickOutsideHandler, { passive: true });
    },
    
    removeNotificationClickOutside() {
      if (this.notificationClickOutsideHandler) {
        document.removeEventListener('click', this.notificationClickOutsideHandler);
        this.notificationClickOutsideHandler = null;
      }
    },
    
    async handleViewNewBookings() {
      this.closeNotificationDropdown();
      
      // Store newBookingIds snapshot before marking as read
      // This ensures NEW tags remain visible even after Read API is called
      const objectId = this.$route.params.id;
      if (objectId) {
        const newBookingIds = notificationService.getNewBookingIds(objectId);
        // Store in sessionStorage so NewBookingsView can use it
        sessionStorage.setItem(`NEW_BOOKING_IDS_${objectId}`, JSON.stringify(newBookingIds));
      }
      
      // Mark notifications as read immediately to remove badge counts
      // But NEW tags will remain until page refresh
      await this.markNotificationsAsRead();
      
      // Navigate to New Bookings
      if (this.$route.params.id) {
        this.$router.push({
          name: 'ObjectDashboard',
          params: { id: this.$route.params.id, objectName: this.objectName },
          query: { tab: 'NewBookingsView' }
        });
      }
    },
    
    async markNotificationsAsRead() {
      try {
        const objectId = this.$route.params.id;
        if (objectId && this.newBookingsCount > 0) {
          await apiService.markBookingNotificationAsRead(objectId);
          // Refresh notifications from service after marking as read
          await notificationService.refreshNotifications();
          // Update local count (will be updated via subscription, but update immediately)
          this.updateNotificationCount();
          // Emit event to parent to refresh notifications
          this.$emit('notifications-read');
        }
      } catch (error) {
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        console.error('Error marking notifications as read:', error);
      }
    },
    
    // Public method to refresh notifications (can be called from parent)
    async refreshNotifications() {
      await this.fetchBookingNotifications();
    },
    
    updateNotificationCount() {
      const objectId = this.$route.params.id;
      if (objectId) {
        this.newBookingsCount = notificationService.getNotificationCount(objectId);
      } else {
        this.newBookingsCount = 0;
      }
    },
    
    setupEventListeners() {
      // Use passive listeners for better performance
      this.clickOutsideHandler = this.handleClickOutside.bind(this);
      document.addEventListener('click', this.clickOutsideHandler, { passive: true });
      document.addEventListener('keydown', this.handleEscapeKey, { passive: true });
    },
    
    removeEventListeners() {
      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler);
        document.removeEventListener('keydown', this.handleEscapeKey);
      }
    },
    
    openEditProfile() {
      this.closeDropdown();
      this.$emit('openEditProfile');
    },

    openManageSubscription() {
      this.closeDropdown();
      this.$emit('openManageSubscription');
    },
    
    handleProfileUpdated(updatedData) {
      this.adminProfileData = updatedData;
      // No need to emit back to parent since DashboardPage handles it directly
    }
  },
  watch: {
    objectName(newVal) {
      if (newVal) {
        this.fetchBookingNotifications();
      }
    },
    '$route.params.id'(newId) {
      if (newId) {
        this.fetchBookingNotifications();
      }
    }
  },
  async mounted() {
    this.loadAdminProfileData();
    // Load notifications from service (force refresh on page load)
    await this.fetchBookingNotifications(true);
    this.setupEventListeners();
    
    // Subscribe to notification updates
    this.unsubscribeNotifications = notificationService.subscribe(() => {
      this.notificationUpdateKey++;
      this.updateNotificationCount();
    });
  },
  
  beforeUnmount() {
    this.removeNotificationClickOutside();
    this.removeEventListeners();
    // Unsubscribe from notifications
    if (this.unsubscribeNotifications) {
      this.unsubscribeNotifications();
    }
  }
};
</script>

<style scoped>

/* Modern Header Styling */
.dashboard-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(91, 46, 142, 0.08);
  box-shadow: 0 4px 20px rgba(91, 46, 142, 0.08);
  padding: 0;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-container {
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  position: relative;
  min-height: 64px;
}

/* Logo Section */
.logo-section {
  flex: 0 0 auto;
  z-index: 2;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
  text-decoration: none;
}

.logo-link:hover {
  transform: scale(1.02);
}

.logo-link:focus {
  outline: 2px solid #5b2e8e;
  outline-offset: 4px;
  border-radius: 4px;
}

.logo-image {
  width: auto;
  height: 40px;
  max-width: 160px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(91, 46, 142, 0.1));
}

/* Object Name Display */
.object-name {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  max-width: 40%;
  text-align: center;
}

.object-title {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #5b2e8e;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 2px rgba(91, 46, 142, 0.1);
}

/* Profile Section */
.profile-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  z-index: 2;
}

.notification-bell-container {
  position: relative;
  margin-right: 0.75rem;
}

.notification-bell-btn {
  position: relative;
  background: rgba(91, 46, 142, 0.08);
  border: none;
  cursor: pointer;
  padding: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: #5b2e8e;
  width: 40px;
  height: 40px;
}

.notification-bell-btn:hover {
  background: rgba(91, 46, 142, 0.12);
  color: #7c3aed;
  transform: translateY(-1px);
}

.notification-bell-btn:active {
  transform: translateY(0);
}

.bell-icon {
  font-size: 18px;
  color: inherit;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-bell-btn:hover .bell-icon {
  transform: scale(1.05);
}

.notification-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-radius: 12px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  padding: 0 5px;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35), 0 0 0 2px white;
  border: none;
  line-height: 1;
  letter-spacing: -0.2px;
}

/* Notification Dropdown Styles */
.notification-dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  z-index: 10000;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 320px;
  max-width: 400px;
  overflow: hidden;
  border: 1px solid rgba(91, 46, 142, 0.1);
}

.notification-dropdown-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.notification-dropdown-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(91, 46, 142, 0.1), rgba(124, 58, 237, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b2e8e;
}

.notification-dropdown-icon svg {
  width: 24px;
  height: 24px;
}

.notification-dropdown-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-dropdown-title {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.notification-dropdown-message {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.5;
}

.notification-dropdown-footer {
  padding: 12px 20px;
  background: rgba(91, 46, 142, 0.02);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.notification-view-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: -0.01em;
}

.notification-view-btn:hover {
  background: linear-gradient(135deg, #7c3aed, #9f7aea);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.notification-view-btn:active {
  transform: translateY(0);
}

.notification-view-btn svg {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.notification-view-btn:hover svg {
  transform: translateX(2px);
}

/* Dropdown Animation */
.notification-dropdown-enter-active,
.notification-dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.notification-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

@media (max-width: 768px) {
  .notification-dropdown-menu {
    right: -16px;
    left: -16px;
    min-width: auto;
    max-width: none;
  }
}

.admin-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.admin-name {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.admin-role {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

/* Profile Dropdown */
.profile-dropdown {
  position: relative;
}

.profile-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}

.profile-button:hover {
  border-color: rgba(91, 46, 142, 0.2);
  background: rgba(91, 46, 142, 0.05);
  transform: translateY(-1px);
}

.profile-button:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.profile-image {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(91, 46, 142, 0.1);
  transition: all 0.2s ease;
}

.profile-button:hover .profile-image {
  border-color: rgba(91, 46, 142, 0.3);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.15);
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
  color: #5b2e8e;
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: 1px solid rgba(91, 46, 142, 0.1);
  border-radius: 12px;
  min-width: 220px;
  padding: 8px;
  box-shadow: 0 10px 40px rgba(91, 46, 142, 0.15), 
              0 4px 12px rgba(91, 46, 142, 0.1);
  z-index: 1001;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  text-decoration: none;
  color: #374151;
  background: none;
  border: none;
  border-radius: 8px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}

.dropdown-item:hover {
  background: rgba(91, 46, 142, 0.08);
  color: #5b2e8e;
  transform: translateX(2px);
}

.dropdown-item:focus {
  background: rgba(91, 46, 142, 0.12);
  color: #5b2e8e;
  box-shadow: 0 0 0 2px rgba(91, 46, 142, 0.2);
}

.logout-item:hover {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
}

.logout-item:focus {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.item-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.dropdown-item:hover .item-icon,
.dropdown-item:focus .item-icon {
  opacity: 1;
}

/* Dropdown Transitions */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-container {
    padding: 12px 16px;
  }
  
  .admin-info {
    display: none;
  }
  
  .profile-section {
    gap: 0;
  }
  
  .object-name {
    max-width: 50%;
  }
  
  .object-title {
    font-size: 16px;
  }
  
  .logo-image {
    height: 36px;
    max-width: 120px;
  }
}

@media (max-width: 480px) {
  .header-container {
    padding: 10px 12px;
    min-height: 56px;
  }
  
  .object-name {
    max-width: 60%;
  }
  
  .object-title {
    font-size: 14px;
  }
  
  .logo-image {
    height: 32px;
    max-width: 100px;
  }
  
  .profile-image {
    width: 32px;
    height: 32px;
    border-radius: 6px;
  }
  
  .dropdown-menu {
    min-width: 160px;
    right: -8px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .dashboard-header {
    border-bottom: 2px solid #5b2e8e;
  }
  
  .dropdown-item:hover {
    background: #5b2e8e;
    color: #ffffff;
  }
}

/* Logout full-screen processing overlay */
.logout-overlay-fade-enter-active,
.logout-overlay-fade-leave-active {
  transition: opacity 0.28s ease;
}

.logout-overlay-fade-enter-active .logout-processing-card,
.logout-overlay-fade-leave-active .logout-processing-card {
  transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.28s ease;
}

.logout-overlay-fade-enter-from,
.logout-overlay-fade-leave-to {
  opacity: 0;
}

.logout-overlay-fade-enter-from .logout-processing-card,
.logout-overlay-fade-leave-to .logout-processing-card {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}

.logout-processing-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.logout-processing-card {
  width: 100%;
  max-width: 380px;
  padding: 2.25rem 2rem 2rem;
  text-align: center;
  background: linear-gradient(165deg, #ffffff 0%, #faf8fc 100%);
  border-radius: 22px;
  border: 1px solid rgba(91, 46, 142, 0.1);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}

.logout-processing-spinner-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 1.35rem;
}

.logout-processing-spinner {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid rgba(91, 46, 142, 0.12);
  border-top-color: #5b2e8e;
  border-right-color: #7c3aed;
  animation: logout-processing-spin 0.85s linear infinite;
}

@keyframes logout-processing-spin {
  to {
    transform: rotate(360deg);
  }
}

.logout-processing-title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.logout-processing-subtitle {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #6b7280;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
    animation: none !important;
  }
}

/* Keep header white in all modes */
@media (prefers-color-scheme: dark) {
  .dashboard-header {
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-bottom-color: rgba(91, 46, 142, 0.08);
  }
  
  .object-title {
    color: #5b2e8e;
  }
  
  .admin-name {
    color: #1a1a1a;
  }
  
  .admin-role {
    color: #6b7280;
  }
  
  .dropdown-menu {
    background: #ffffff;
    border-color: rgba(91, 46, 142, 0.1);
  }
  
  .dropdown-item {
    color: #374151;
  }
  
  .dropdown-item:hover {
    background: rgba(91, 46, 142, 0.08);
    color: #5b2e8e;
  }
}
</style>